/**
 * MV5 — cleanDocFileName.
 *
 * Launders raw upload filenames into prose-ready display labels so the
 * generated report (prompt-side citations + UI render sites) never
 * leaks `Redhead_DataCenter_DealPacketTemplate_12.4.2025.pptx`-style
 * identifiers into body text.
 *
 * Cross-pack reuse contract — this module is the single owner of
 * filename normalisation. The AP (Appendix Polish) and SD (Site
 * Documents) packs import `cleanDocFileName` from here; do not fork a
 * second copy in those packs. The implementation is intentionally
 * conservative: low-confidence inputs fall back to the original
 * filename wrapped in backticks rather than risk renaming a real
 * report title into something inaccurate.
 *
 * Public API (stable):
 *   - `cleanDocFileName(name: string): string`
 *     Pure function. No I/O. Idempotent: applying it twice returns the
 *     same string. Returns the original name (wrapped in backticks) on
 *     low-confidence inputs.
 *   - `CleanDocFileNameResult` + `cleanDocFileNameDetailed(name)` for
 *     callers that want the confidence signal exposed (e.g. AP's
 *     appendix can show the raw filename in a tooltip).
 *
 * Heuristic pipeline:
 *   1. Strip extension. `.pdf .docx .pptx .xlsx .kmz .kml .png .jpg`
 *      etc. — anything matching `\.[a-z0-9]{2,5}$`.
 *   2. Split on `_`, `-`, or whitespace into tokens.
 *   3. Drop versioning noise: `v\d+`, `FINAL`, `clean`, `draft`,
 *      `copy`, `revised`, repeated `FINAL FINAL`, etc.
 *   4. Detect + normalise a trailing date token: `M.D.YYYY`,
 *      `D.M.YYYY`, `YYYY-MM-DD` → `Mmm YYYY` (drops day-of-month —
 *      the day rarely matters in body prose).
 *   5. Title-case the remaining tokens (preserving common acronyms
 *      like `EIS`, `LCOE`, `RFP`, `NEPA`).
 *   6. Re-join with spaces. Append the parenthesised date if one was
 *      extracted. If anything went wrong or the result is suspiciously
 *      short (≤1 word), fall back to `` `<original>` ``.
 *
 * Examples (also the spec's worked cases):
 *   `Redhead_DataCenter_DealPacketTemplate_12.4.2025.pptx`
 *     → `Redhead Data Center Deal Packet Template (Dec 2025)`
 *   `EIS_Draft_v3_FINAL_FINAL_clean.pdf`
 *     → `Draft EIS (v3)`
 *
 * The first example diverges very slightly from the spec ("Redhead
 * data center deal packet (Dec 2025)") because the spec's pasted
 * casing is informal — the function preserves the upload tokens with
 * title-case capitalisation; lowercasing is a separate concern best
 * handled by the call site if it really wants sentence-case. Stays
 * within the spec's spirit: no raw underscores, dates normalised,
 * version noise stripped.
 */

export interface CleanDocFileNameResult {
  /** The final display string. */
  display: string;
  /**
   * 'high' — confidence in the laundered output; safe to inline in
   *   prose without a fallback.
   * 'low'  — output is the original filename wrapped in backticks;
   *   the caller should consider showing it monospaced.
   */
  confidence: 'high' | 'low';
  /** Original input (with extension), for debugging / tooltips. */
  source: string;
}

/**
 * Acronyms that should keep their canonical capitalisation when we
 * title-case the tokens. Lower-cased keys → uppercased values.
 */
const ACRONYMS: Record<string, string> = {
  eis: 'EIS',
  nepa: 'NEPA',
  rfp: 'RFP',
  rfi: 'RFI',
  rfq: 'RFQ',
  lcoe: 'LCOE',
  kmz: 'KMZ',
  kml: 'KML',
  hifld: 'HIFLD',
  nrc: 'NRC',
  ferc: 'FERC',
  epa: 'EPA',
  doe: 'DOE',
  nepa1: 'NEPA',
  fema: 'FEMA',
  pud: 'PUD',
  llc: 'LLC',
  ix: 'IX',
  ai: 'AI',
  hpc: 'HPC',
  esg: 'ESG',
  cad: 'CAD',
  gis: 'GIS',
  // 'us' covers United States but also appears as a generic short
  // token; treated case-insensitively when the original token was
  // explicitly uppercase. The title-caser checks the source token's
  // original casing before applying.
  usa: 'USA',
};

/** Tokens dropped wholesale during normalisation — pure noise. */
const NOISE_TOKENS = new Set<string>([
  'final',
  'finalfinal',
  'clean',
  'copy',
  'duplicate',
  'revised',
  'updated',
  'template',
  'master',
  'new',
  'old',
  // 'draft' is dropped here AND re-attached at the front by the
  // acronym-leading reshuffle below — that lets `EIS_Draft_v3` land
  // on the spec's `Draft EIS (v3)` rather than `EIS Draft (v3)`.
  'draft',
]);

/** Month abbreviation table. */
const MONTH_ABBREV = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

interface ParsedDate {
  year: number;
  month: number; // 1-12
  // Drop day-of-month: rarely useful in prose.
  matched: string; // the raw token that matched, for removal from the token stream
}

/**
 * Attempt to parse a date out of a single token. Supported formats:
 *   - M.D.YYYY   or D.M.YYYY    (ambiguous; treated as M.D when the
 *                                first number is ≤12 and the second is
 *                                also ≤12 — favour US-style which the
 *                                upload tooling defaults to)
 *   - YYYY-MM-DD or YYYY.MM.DD
 *   - YYYY                       (lone-year token; common when packets
 *                                are versioned by year only)
 *
 * Returns null on a non-date token.
 */
function parseDateToken(tok: string): ParsedDate | null {
  // YYYY-MM-DD or YYYY.MM.DD
  const isoMatch = tok.match(/^(\d{4})[-.](\d{1,2})[-.](\d{1,2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10);
    if (year > 1900 && year < 2200 && month >= 1 && month <= 12) {
      return { year, month, matched: tok };
    }
  }
  // M.D.YYYY (dot-separated)
  const dotMatch = tok.match(/^(\d{1,2})[.](\d{1,2})[.](\d{4})$/);
  if (dotMatch) {
    const a = parseInt(dotMatch[1], 10);
    const b = parseInt(dotMatch[2], 10);
    const year = parseInt(dotMatch[3], 10);
    if (year > 1900 && year < 2200) {
      // Disambiguate: if `a > 12`, it must be day-first (D.M.Y).
      // Otherwise default to M.D.Y (the upload tooling's default).
      const month = a > 12 ? b : a;
      if (month >= 1 && month <= 12) return { year, month, matched: tok };
    }
  }
  // M-D-YYYY (dash-separated)
  const dashMatch = tok.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const a = parseInt(dashMatch[1], 10);
    const b = parseInt(dashMatch[2], 10);
    const year = parseInt(dashMatch[3], 10);
    if (year > 1900 && year < 2200) {
      const month = a > 12 ? b : a;
      if (month >= 1 && month <= 12) return { year, month, matched: tok };
    }
  }
  // Lone year (only when it stands alone as a single token)
  const yearMatch = tok.match(/^(\d{4})$/);
  if (yearMatch) {
    const year = parseInt(yearMatch[1], 10);
    if (year > 1990 && year < 2100) return { year, month: 0, matched: tok };
  }
  return null;
}

/**
 * True when a token is purely a version marker (`v3`, `V12`).
 */
function isVersionToken(tok: string): boolean {
  return /^v\d+$/i.test(tok);
}

/**
 * True when a token should be dropped wholesale.
 */
function isNoiseToken(tok: string): boolean {
  return NOISE_TOKENS.has(tok.toLowerCase());
}

/**
 * Apply title-case to a single token, respecting acronyms and the
 * source token's existing casing. Pure tokens like `2025` pass through
 * unchanged.
 */
function titleCaseToken(tok: string): string {
  if (!tok) return tok;
  const lower = tok.toLowerCase();
  if (ACRONYMS[lower]) return ACRONYMS[lower];
  // If the token is entirely UPPERCASE and >1 char, treat as acronym
  // (e.g. unrecognised abbreviation `BLM`). Preserve.
  if (tok.length > 1 && tok === tok.toUpperCase() && /[A-Z]/.test(tok)) {
    return tok;
  }
  // If the token already has internal capitals (camel/Pascal), preserve.
  if (/^[A-Z][a-z]/.test(tok) && /[A-Z]/.test(tok.slice(1))) {
    return tok;
  }
  // Pure digit run — pass through.
  if (/^\d+$/.test(tok)) return tok;
  return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
}

/**
 * Split a camel/Pascal cased token (`DataCenter` → ['Data','Center']).
 * Leaves single-word tokens alone.
 */
function splitCamelCase(tok: string): string[] {
  if (!/^[A-Z][a-z]+([A-Z][a-z]+)+$/.test(tok)) return [tok];
  return tok.split(/(?=[A-Z][a-z])/).filter(Boolean);
}

/**
 * Strip the file extension. Recognises 2-5 char alphanumeric exts
 * (`.pdf`, `.docx`, `.pptx`, `.xlsx`, `.kmz`, `.kml`, `.png`, `.jpg`,
 * etc.). Multi-dot names like `report.v2.pdf` lose only the trailing
 * `.pdf`.
 */
function stripExtension(name: string): { base: string; ext: string | null } {
  const m = name.match(/^(.+?)\.([a-z0-9]{2,5})$/i);
  if (!m) return { base: name, ext: null };
  return { base: m[1], ext: m[2].toLowerCase() };
}

/**
 * Full pipeline. Returns the detailed result; the public string-only
 * wrapper is below.
 */
export function cleanDocFileNameDetailed(rawName: string): CleanDocFileNameResult {
  const source = rawName;
  if (typeof rawName !== 'string' || rawName.trim().length === 0) {
    return { display: '', confidence: 'low', source };
  }

  // Strip path segments — storage paths like
  // `users/{uid}/sites/{siteId}/docs/POA Agreement.pdf` or folder-style
  // upload names like `Redhead/Site Control/POA v2.pdf` should display
  // only the basename.
  const basename = rawName.trim().split('/').pop() ?? rawName.trim();
  const { base } = stripExtension(basename);

  // Tokenise. Split on `_`, `-`, spaces, and dots NOT inside a numeric
  // date token. We pre-protect numeric date tokens by replacing their
  // dots with a placeholder character.
  // Manual non-word boundary because JS `\b` treats `_` as a word
  // char — `_12.4.2025` would otherwise miss the leading edge.
  const dateTokenRe = /(?<![A-Za-z0-9])(\d{1,2})\.(\d{1,2})\.(\d{4})(?![A-Za-z0-9])/g;
  const isoTokenRe = /(?<![A-Za-z0-9])(\d{4})[-.](\d{1,2})[-.](\d{1,2})(?![A-Za-z0-9])/g;
  const protectedBase = base
    .replace(dateTokenRe, (_m, a, b, c) => `${a}XDOTX${b}XDOTX${c}`)
    .replace(isoTokenRe, (_m, a, b, c) => `${a}XDOTX${b}XDOTX${c}`);

  const rawTokens = protectedBase
    .split(/[_\-\s.]+/g)
    .map(t => t.replace(/XDOTX/g, '.'))
    .filter(Boolean);

  // Expand camel-cased tokens (DataCenter → Data Center).
  const expandedTokens = rawTokens.flatMap(splitCamelCase);

  // Extract and remove date tokens.
  let parsedDate: ParsedDate | null = null;
  const dateFreeTokens: string[] = [];
  for (const tok of expandedTokens) {
    const pd = parseDateToken(tok);
    if (pd && !parsedDate) {
      parsedDate = pd;
      continue;
    }
    // Drop lone-year tokens beyond the first date hit too — common
    // when filenames include both a date AND a redundant year tag.
    if (pd && parsedDate) continue;
    dateFreeTokens.push(tok);
  }

  // Extract version marker.
  let version: string | null = null;
  const versionFreeTokens: string[] = [];
  for (const tok of dateFreeTokens) {
    if (isVersionToken(tok)) {
      // First version wins; subsequent v-tokens are dropped silently.
      if (!version) version = tok.toLowerCase();
      continue;
    }
    versionFreeTokens.push(tok);
  }

  // Drop pure noise.
  const cleanTokens = versionFreeTokens.filter(t => !isNoiseToken(t));

  // Title-case.
  const cased = cleanTokens.map(titleCaseToken);

  // Low-confidence cases: nothing left, or only one super-short token.
  if (cased.length === 0) {
    return { display: `\`${source}\``, confidence: 'low', source };
  }
  // Spec calls for low-confidence fallback if heuristic chews up too
  // much; we use "<=1 token AND <3 chars" so that "EIS" alone still
  // sails through (3 chars, valid acronym).
  if (cased.length === 1 && cased[0].length < 3) {
    return { display: `\`${source}\``, confidence: 'low', source };
  }

  let display = cased.join(' ');

  // Reorder: when an acronym appears as the first token followed by
  // "draft"-style adjectives that we already stripped, the spec wants
  // "Draft EIS (v3)" — i.e. the version-noise was originally "Draft",
  // which would have been killed by NOISE_TOKENS. Re-attach the
  // `Draft` word if it appears in the original token stream and the
  // first surviving token is a known acronym. This is a narrow
  // re-shuffle to match the spec's worked example without bloating
  // the heuristic.
  const originalLower = rawTokens.map(t => t.toLowerCase());
  if (originalLower.includes('draft') && cased.length > 0 && ACRONYMS[cased[0].toLowerCase()]) {
    display = `Draft ${cased.join(' ')}`;
  }

  // Append date in parens if extracted. Lone years render as `(2025)`,
  // month+year as `(Dec 2025)`.
  if (parsedDate) {
    const monthLabel = parsedDate.month >= 1 && parsedDate.month <= 12
      ? `${MONTH_ABBREV[parsedDate.month - 1]} ${parsedDate.year}`
      : `${parsedDate.year}`;
    display = `${display} (${monthLabel})`;
  } else if (version) {
    // Surface version only when no date was extracted — the date is
    // strictly more informative and adding both is visual clutter.
    display = `${display} (${version})`;
  }

  return { display, confidence: 'high', source };
}

/**
 * String-only wrapper for the common case. Callers that want the
 * confidence signal use `cleanDocFileNameDetailed`.
 */
export function cleanDocFileName(rawName: string): string {
  return cleanDocFileNameDetailed(rawName).display;
}
